/* eslint-disable no-nested-ternary */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Table, { TColumn } from '~/components/common/Table/Table';
import { convertedSortParams, downloadCSV } from '~/utils/common';
import { alertParams, showAlert } from '~/components/common/Alert';
import { IAlert } from '~/components/common/Alert/Alert';
import { useDeleteAddressMutation } from '~/services/api/addresss';
import { queryClient } from '~/lib/react-query';
import { IAddress } from '~/models/address';
import { fromQueryString } from '~/utils/queryString';
import themes, { styled } from '~/themes';
import { ToolTipText } from '~/components/common/Tooltip/TooltipText';
import { ElementSelect } from '~/components/common/ElementSelect/ElementSelect';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Stack, Typography } from '@mui/material';
import CheckBox from '~/components/common/CheckBox';
import { uniqBy } from 'lodash';
import CloseIcon from '~/assets/images/icons/close-white.svg';
import CheckboxIcon from '~/assets/images/icons/checkbox.svg';
import Button from '~/components/common/Button';
import { addressBookQueryKeys, useExportAddressBooks } from '../apis';

const EmptyStateData = [
  {
    name: 'Frequent Customer',
    phone_formatted: '(+1) 101-010-1010',
    address_1: '1010 Kingsway',
    city: 'Burnaby',
    state_name: 'British Columbia',
    postcode: 'V0H 0B0',
    email: 'test@example.com',
  },
];

export const StyledEmptyStateContainer = styled('div')(({ theme }) => ({
  'position': 'absolute',
  'display': 'flex',
  'flexDirection': 'column',
  'alignItems': 'center',
  'justifyContent': 'center',
  'padding': theme.spacing(2),
  'height': `${theme.spacing(36)}`,
  'width': `calc(100% - ${theme.spacing(5)})`,
  'zIndex': 2,
  '& > h1': {
    marginTop: theme.spacing(3),
    fontSize: 14,
    paddingTop: 0,
    fontWeight: 400,
  },
  [theme.breakpoints.down('md')]: {
    '& > img:first-of-type': {
      maxWidth: '100%',
    },
  },
}));

interface AddressBookTableProps {
  data?: IAddress[];
  // eslint-disable-next-line no-unused-vars
  handleSort: (sortId: string, nextArrange: number) => void;
  loading: boolean;
  onEdit: (item: IAddress) => void;
  isEmptyState?: boolean;
  renderEmpty?: React.ReactNode;
}

export const AddressBookTable: React.FC<AddressBookTableProps> = ({
  data,
  handleSort,
  loading,
  onEdit,
  isEmptyState,
  renderEmpty,
}) => {
  const location = useLocation();
  const lastRowRef = useRef<HTMLDivElement | null>(null);

  const { sort, sort_by } = fromQueryString(location.search);
  const { mutate: deleteAddressMutation } = useDeleteAddressMutation({
    onSuccess: () => {
      showAlert({ ...alertParams.success, title: 'Deleted successfully' });
      queryClient.invalidateQueries(addressBookQueryKeys.getAddressBookList());
    },
  });

  const [addressSelected, setAddressSelected] = useState<IAddress[]>([]);

  const addressIdsSelected = useMemo(
    () => addressSelected?.map((address) => address.id),
    [addressSelected],
  );

  const checkAllAddress = useMemo(
    () => data.every((x) => addressIdsSelected.includes(x.id)),
    [data, addressIdsSelected],
  );

  const isIndeterminate = useMemo(() => {
    const selectedAddressInPage = data.filter((x) =>
      addressIdsSelected.includes(x.id),
    );
    return (
      selectedAddressInPage.length > 0 &&
      selectedAddressInPage.length < data.length
    );
  }, [data, addressIdsSelected]);

  const handleDownloadCSV = (csvString: string) => {
    downloadCSV(csvString, 'Export_Address_Book');
  };

  const { mutate: exportAddress, isLoading: isLoadingExport } =
    useExportAddressBooks({
      onSuccess: (csvData) => {
        handleDownloadCSV(csvData?.csv);
        setAddressSelected([]);
      },
    });

  const onDeleteAlert = useCallback((id: string) => {
    const params: IAlert = {
      ...alertParams.warning,
      title: 'Are you sure you want to delete?',
      description: 'The contact will be removed from address book.',
      cancelText: 'Cancel',
      okText: 'Yes',
      onOk: () => deleteAddressMutation(id),
    };
    showAlert(params);
  }, []);

  const renderAction = useCallback(
    (address: IAddress) => (
      <ElementSelect
        paperProps={{
          sx: {
            width: 160,
          },
        }}
        elementSelect={() => (
          <MoreHorizIcon sx={{ fontSize: 20, color: themes.color.violet900 }} />
        )}
        iconButtonProps={{
          sx: { height: 26, width: 26 },
        }}
        onChange={(o) => {
          if (o.value === 'edit') {
            onEdit(address);
          }
          if (o.value === 'delete') {
            onDeleteAlert(address.id);
          }
        }}
        options={[
          {
            label: 'Edit contact',
            value: 'edit',
          },
          {
            label: 'Delete contact',
            value: 'delete',
          },
        ]}
      />
    ),
    [],
  );

  const renderName = (item: IAddress, _, index: number) => {
    const isLastItem = index === data.length - 1;

    return (
      <Stack
        ref={isLastItem ? lastRowRef : null}
        sx={{
          'display': 'flex',
          'flexDirection': 'row',
          'alignItems': 'center',
          '& .MuiCheckbox-root': {
            width: '36px',
            height: '36px',
          },
        }}
      >
        <CheckBox
          defaultCheckedIcon
          checked={addressIdsSelected?.includes(item.id)}
          disabled={isEmptyState}
          onChange={(event) => {
            const newVal = event.target.checked;
            if (newVal) {
              setAddressSelected((prev) => [...prev, item]);
              if (isLastItem && addressIdsSelected?.length === 0) {
                setTimeout(() => {
                  lastRowRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                  });
                }, 150);
              }
            } else {
              setAddressSelected((prev) =>
                prev.filter((address) => address.id !== item.id),
              );
            }
          }}
        />
        <ToolTipText text={item.name} maxLength={30} />
      </Stack>
    );
  };

  const columns: TColumn[] = useMemo(
    () => [
      {
        id: 'name',
        title: (
          <Stack
            sx={{
              'display': 'flex',
              'flexDirection': 'row',
              'alignItems': 'center',
              '& .MuiCheckbox-root': {
                width: '36px',
                height: '36px',
              },
            }}
          >
            <CheckBox
              defaultCheckedIcon
              checked={checkAllAddress}
              onChange={(event) => {
                const newVal = event.target.checked;
                if (newVal) {
                  setAddressSelected((prev) =>
                    uniqBy([...prev, ...data], 'id'),
                  );
                } else {
                  setAddressSelected((prev) =>
                    prev.filter(
                      (current) =>
                        data.findIndex((x) => x.id === current.id) === -1,
                    ),
                  );
                }
              }}
              indeterminate={isIndeterminate}
            />
            Name
          </Stack>
        ),
        sortable: true,
        cellStyle: {
          minWidth: 200,
        },
        renderComponent: renderName,
      },
      {
        id: 'phone_formatted',
        title: 'Phone No.',
        cellStyle: { minWidth: 120 },
      },
      {
        id: 'email',
        title: 'Email',
        cellStyle: { minWidth: 120 },
        renderComponent: (row) => (
          <ToolTipText
            text={row.email}
            maxLength={30}
            textNode={(v) => v || '-'}
          />
        ),
      },
      {
        id: 'address_1',
        title: 'Address',
        cellStyle: { minWidth: 120 },
        renderComponent: (row) => (
          <ToolTipText text={row.address_1} maxLength={30} />
        ),
      },
      {
        id: 'city',
        title: 'City',
        sortable: true,
        cellStyle: { minWidth: 120 },
      },
      {
        id: 'state_name',
        title: 'Province/State',
        sortable: true,
        cellStyle: { minWidth: 80 },
      },
      {
        id: 'postcode',
        title: 'Postal code',
        cellStyle: { minWidth: 80 },
      },
      {
        id: 'action',
        title: '',
        cellStyle: {
          minWidth: 50,
          textAlign: 'right',
        },
        renderComponent: renderAction,
      },
    ],
    [addressSelected, checkAllAddress, isIndeterminate, data],
  );

  const renderAddressSelected = () => (
    <Stack
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        borderRadius: '2px',
        backgroundColor: themes.color.violet700,
        padding: 1.5,
        zIndex: 10,
        [themes.breakpoints.down('sm')]: {
          padding: 0.8,
        },
      }}
    >
      <Stack
        flexDirection='row'
        alignItems='center'
        gap={0.5}
        px={1.6}
        sx={{
          color: 'white',
          fontWeight: 500,
          cursor: 'pointer',
          [themes.breakpoints.down('md')]: {
            px: 0.8,
          },
        }}
        onClick={() => {
          setAddressSelected([]);
        }}
      >
        <img
          src={CloseIcon}
          alt=''
          style={{
            width: 9,
            height: 9,
          }}
        />
        Discard
      </Stack>
      <Stack
        flexDirection='row'
        alignItems='center'
        gap={0.8}
        pl={1.6}
        sx={{
          color: 'white',
          fontWeight: 500,
          cursor: 'pointer',
          [themes.breakpoints.down('sm')]: {
            paddingLeft: 0.8,
          },
        }}
      >
        <Typography
          px={1.6}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 0.8,
            [themes.breakpoints.down('md')]: {
              px: 0.2,
            },
          }}
        >
          <img src={CheckboxIcon} alt='' />
          {addressIdsSelected.length} selected
        </Typography>
        <Button
          noRounder
          buttonType='text'
          onClick={() =>
            exportAddress({
              ids: addressIdsSelected,
            })
          }
          sx={{
            [themes.breakpoints.down('md')]: {
              minWidth: 'unset !important',
              padding: '4px 8px',
            },
            minWidth: '76px !important',
            backgroundColor: 'white !important',
            color: `${themes.color.violet700} !important`,
            padding: '4px 16px',
            borderRadius: '8px !important',
          }}
          loading={isLoadingExport}
        >
          <Typography
            sx={{
              fontWeight: 500,
            }}
          >
            Export
          </Typography>
        </Button>
      </Stack>
    </Stack>
  );

  const renderEmptyState = useMemo(() => {
    if (isEmptyState) {
      return (
        <StyledEmptyStateContainer>{renderEmpty}</StyledEmptyStateContainer>
      );
    }
    return null;
  }, [isEmptyState, renderEmpty]);

  return (
    <>
      <Table
        columns={columns}
        dataProps={isEmptyState ? EmptyStateData : data}
        sortBy={convertedSortParams(sort, sort_by)}
        onSort={handleSort}
        loading={loading}
        noHoverInteraction
        striped={false}
        customEmptyState={renderEmptyState}
        sxNoData={{ padding: '35px !important' }}
        headerCellSxProps={{
          'color': themes.color.gray400,
          'backgroundColor': themes.color.gray200,
          'fontSize': 12,
          'paddingTop': '4px !important',
          'paddingBottom': '4px !important',
          'border': 'none',
          '& #name-sortable': {
            display: 'flex !important',
          },
        }}
        cellSxProps={{
          paddingTop: '10px !important',
          paddingBottom: '10px !important',
          color: themes.color.violet500,
        }}
        containerSxProps={{
          ...(isEmptyState
            ? {
                '& td': {
                  filter: 'grayscale(1)',
                  opacity: 0.25,
                  pointerEvents: 'none',
                },
              }
            : {}),
        }}
      />
      {addressIdsSelected?.length !== 0 && renderAddressSelected()}
    </>
  );
};
